import { useEffect, useState } from 'react'
import {
  Modal,
  ModalDialog,
  DialogTitle,
  DialogContent,
  Stack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Button,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/joy'
import { useQuery } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { paymentsApi } from '../../lib/billingApi'

interface SendReceiptModalProps {
  open: boolean
  paymentId: number | null
  onClose: () => void
}

/**
 * Lets the user preview and edit the receipt email (recipient/subject/message)
 * before it is sent. The body is prefilled from the backend draft; a PDF receipt
 * is attached automatically on the server.
 */
export default function SendReceiptModal({ open, paymentId, onClose }: SendReceiptModalProps) {
  const [toEmail, setToEmail] = useState('')
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [sending, setSending] = useState(false)

  const { data: draft, isLoading, isError, refetch } = useQuery({
    queryKey: ['receipt-draft', paymentId],
    queryFn: () => paymentsApi.getReceiptDraft(paymentId as number),
    enabled: open && paymentId != null,
  })

  useEffect(() => {
    if (draft) {
      setToEmail(draft.to_email || '')
      setSubject(draft.subject || '')
      setMessage(draft.message || '')
    }
  }, [draft])

  const handleSend = async () => {
    if (paymentId == null) return
    if (!toEmail.trim()) {
      toast.error('Please enter a recipient email')
      return
    }
    setSending(true)
    try {
      const res = await paymentsApi.sendReceipt(paymentId, {
        to_email: toEmail.trim(),
        subject,
        message,
      })
      toast.success(res?.message || 'Receipt sent')
      onClose()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to send receipt')
    } finally {
      setSending(false)
    }
  }

  return (
    <Modal open={open} onClose={() => (!sending ? onClose() : undefined)}>
      <ModalDialog sx={{ maxWidth: 560, width: 'calc(100% - 32px)', maxHeight: 'calc(100dvh - 32px)', overflowY: 'auto' }}>
        <DialogTitle>Send Receipt</DialogTitle>
        <DialogContent sx={{ overflow: 'visible' }}>
          {isLoading ? (
            <Stack alignItems="center" sx={{ py: 4 }}>
              <CircularProgress size="sm" />
              <Typography level="body-sm" sx={{ mt: 1 }}>
                Loading…
              </Typography>
            </Stack>
          ) : isError ? (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Alert color="danger">The payment is saved, but the receipt email could not be loaded.</Alert>
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button variant="plain" color="neutral" onClick={onClose}>Close</Button>
                <Button onClick={() => refetch()}>Try again</Button>
              </Stack>
            </Stack>
          ) : (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <FormControl required>
                <FormLabel>To</FormLabel>
                <Input
                  type="email"
                  value={toEmail}
                  onChange={(e) => setToEmail(e.target.value)}
                  placeholder="customer@example.com"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Subject</FormLabel>
                <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
              </FormControl>
              <FormControl>
                <FormLabel>Message</FormLabel>
                <Textarea minRows={6} value={message} onChange={(e) => setMessage(e.target.value)} />
              </FormControl>
              <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                A PDF receipt will be attached automatically.
              </Typography>
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button variant="plain" color="neutral" onClick={onClose} disabled={sending}>
                  Cancel
                </Button>
                <Button onClick={handleSend} loading={sending}>
                  Send Receipt
                </Button>
              </Stack>
            </Stack>
          )}
        </DialogContent>
      </ModalDialog>
    </Modal>
  )
}
